import { clsx } from 'clsx';
import { useRef } from 'react';
import type { KeyboardEvent } from 'react';
import {
  PAYMENT_METHOD_LABEL,
  PAYMENT_METHOD_ORDER,
  type PaymentMethod,
} from '../../types/payment';
import { PaymentMethodIcon } from './PaymentMethodIcons';

type PaymentMethodTabsProps = {
  value: PaymentMethod;
  onChange: (next: PaymentMethod) => void;
  className?: string;
};

/**
 * Segmented control cerimonial com keyboard nav (Arrow/Home/End) +
 * roving tabindex. Mobile faz scroll-x snap.
 */
export function PaymentMethodTabs({ value, onChange, className }: PaymentMethodTabsProps) {
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);

  function focusIndex(idx: number) {
    const clamped = (idx + PAYMENT_METHOD_ORDER.length) % PAYMENT_METHOD_ORDER.length;
    const target = buttonsRef.current[clamped];
    if (target) {
      target.focus();
      onChange(PAYMENT_METHOD_ORDER[clamped]);
    }
  }

  function handleKeyDown(idx: number, e: KeyboardEvent<HTMLButtonElement>) {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        focusIndex(idx + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        focusIndex(idx - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusIndex(0);
        break;
      case 'End':
        e.preventDefault();
        focusIndex(PAYMENT_METHOD_ORDER.length - 1);
        break;
    }
  }

  return (
    <div
      role="tablist"
      aria-label="Métodos de pagamento"
      className={clsx(
        // Mobile: scroll horizontal com snap
        '-mx-2 flex snap-x snap-mandatory gap-2 overflow-x-auto px-2 pb-1 scrollbar-none',
        // Desktop: grid de 5 colunas, sem scroll
        'sm:mx-0 sm:grid sm:snap-none sm:grid-cols-5 sm:gap-2 sm:overflow-visible sm:px-0 sm:pb-0',
        className,
      )}
      style={{ scrollbarWidth: 'none' }}
    >
      {PAYMENT_METHOD_ORDER.map((m, i) => {
        const active = value === m;
        return (
          <button
            key={m}
            ref={(el) => {
              buttonsRef.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`pm-tab-${m}`}
            aria-selected={active}
            aria-controls={`pm-panel-${m}`}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(m)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            className={clsx(
              'group flex min-w-[84px] flex-shrink-0 snap-start flex-col items-center justify-center gap-1.5',
              'rounded-md border px-3 py-3 transition-colors duration-fast sm:min-w-0',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold',
              active
                ? 'border-gold bg-gold/10 text-gold shadow-goldSoft'
                : 'border-gold/20 text-champagne hover:border-gold/50 hover:text-parchment',
            )}
          >
            <PaymentMethodIcon name={m} className="h-5 w-5" aria-hidden="true" />
            <span className="font-mono text-[10px] uppercase tracking-stamp">
              {PAYMENT_METHOD_LABEL[m]}
            </span>
          </button>
        );
      })}
    </div>
  );
}
